import { useCallback, useEffect, useRef, useState } from "react";
import { Fingerprint, Lock, Delete, Bell } from "lucide-react";

/* Lock screen + passcode. Methods: 4-digit PIN pad, 3x3 pattern (line-art), thumb scan
   (press and hold). Wrong entries shake; three fails lock the pad for 10 seconds.
   PinPad and PatternPad are exported for the Settings enrolment flow. */

const PIN_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "del"];

export function PinPad({ entry, onDigit, onDelete, accent, disabled }) {
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="flex gap-3">
        {[0, 1, 2, 3].map((i) => (
          <span key={i} data-testid={`pin-dot-${i}`} className={`w-3.5 h-3.5 rounded-full border-2 transition-colors ${entry.length > i ? "" : "border-white/30"}`}
            style={entry.length > i ? { backgroundColor: accent, borderColor: accent } : {}} />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2.5">
        {PIN_KEYS.map((k, i) => (
          k === "" ? <span key={i} /> : (
            <button key={i} data-testid={k === "del" ? "pin-del" : `pin-key-${k}`} disabled={disabled}
              onClick={() => (k === "del" ? onDelete() : onDigit(k))}
              className="w-16 h-16 rounded-full bg-white/8 border border-white/10 font-sans text-xl text-white flex items-center justify-center transition-all active:scale-90 active:bg-white/20 disabled:opacity-30">
              {k === "del" ? <Delete className="w-5 h-5" /> : k}
            </button>
          )
        ))}
      </div>
    </div>
  );
}

export function PatternPad({ points, onToggle, accent, disabled, size = "w-64" }) {
  // 9 dots; points = ordered array of tapped dot indexes; lines connect them
  const pts = points.map((p) => ({ x: (p % 3) * 33.33 + 16.67, y: Math.floor(p / 3) * 33.33 + 16.67 }));
  return (
    <div className={`relative ${size} aspect-square mx-auto`}>
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full pointer-events-none">
        {pts.length > 1 && (
          <polyline points={pts.map((p) => `${p.x},${p.y}`).join(" ")} fill="none"
            stroke={accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
        )}
      </svg>
      <div className="absolute inset-0 grid grid-cols-3 grid-rows-3">
        {Array.from({ length: 9 }, (_, i) => {
          const on = points.includes(i);
          return (
            <button key={i} data-testid={`pattern-dot-${i}`} disabled={disabled || on} onClick={() => onToggle(i)}
              className="flex items-center justify-center transition-transform active:scale-90 disabled:cursor-default">
              <span className={`rounded-full transition-all ${on ? "w-5 h-5" : "w-4 h-4 bg-white/20"}`}
                style={on ? { backgroundColor: accent, boxShadow: `0 0 12px ${accent}` } : {}} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function ThumbScan({ onDone, accent, disabled, label = "hold to scan" }) {
  const [prog, setProg] = useState(0);
  const timer = useRef(null);
  const start = () => {
    if (disabled) return;
    const t0 = Date.now();
    timer.current = setInterval(() => {
      const p = (Date.now() - t0) / 1200;
      if (p >= 1) { clearInterval(timer.current); setProg(1); setTimeout(onDone, 200); }
      else setProg(p);
    }, 50);
  };
  const cancel = () => { clearInterval(timer.current); setProg(0); };
  useEffect(() => () => clearInterval(timer.current), []);
  return (
    <div className="flex flex-col items-center gap-3">
      <button data-testid="thumb-scan" disabled={disabled}
        onPointerDown={start} onPointerUp={cancel} onPointerLeave={cancel}
        className="relative w-24 h-24 rounded-full border border-white/15 bg-white/5 flex items-center justify-center transition-transform active:scale-95 disabled:opacity-40 select-none"
        style={{ background: `conic-gradient(${accent} ${prog * 360}deg, rgba(255,255,255,0.06) 0deg)` }}>
        <span className="absolute inset-1.5 rounded-full bg-[#0a0a0a] flex items-center justify-center">
          <Fingerprint className="w-10 h-10 transition-colors" style={{ color: prog > 0 ? accent : "rgba(255,255,255,0.5)" }} />
        </span>
      </button>
      <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-white/50">{label}</p>
    </div>
  );
}

export default function LockScreen({ profile, accent, sfx, unread = 0, mailUnread = 0, onUnlock }) {
  const method = profile?.security?.method || "pin";
  const code = profile?.security?.code || "";
  const [stage, setStage] = useState("cover");
  const [pin, setPin] = useState("");
  const [pat, setPat] = useState([]);
  const [fails, setFails] = useState(0);
  const [cool, setCool] = useState(0);
  const [shake, setShake] = useState(false);
  const [now, setNow] = useState(new Date());

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { if (cool > 0) { const t = setTimeout(() => setCool(cool - 1), 1000); return () => clearTimeout(t); } }, [cool]);

  const fail = useCallback(() => {
    sfx?.error?.();
    setShake(true);
    setTimeout(() => setShake(false), 500);
    setPin(""); setPat([]);
    setFails((f) => { if (f + 1 >= 3) { setCool(10); return 0; } return f + 1; });
  }, [sfx]);

  const succeed = useCallback(() => { sfx?.success?.(); onUnlock(); }, [sfx, onUnlock]);

  const digit = (d) => {
    const next = (pin + d).slice(0, 4);
    setPin(next);
    sfx?.keyTick?.();
    if (next.length === 4) setTimeout(() => (next === code ? succeed() : fail()), 150);
  };
  const toggleDot = (i) => {
    const next = [...pat, i];
    setPat(next);
    sfx?.keyTick?.();
    if (next.length === Math.max(3, code.split(",").length)) {
      setTimeout(() => (next.join(",") === code ? succeed() : fail()), 200);
    }
  };

  const shakeStyle = shake ? { animation: "phShake 0.45s ease" } : {};

  return (
    <div data-testid="lock-screen" className="absolute inset-0 z-30 bg-black/85 backdrop-blur-xl flex flex-col items-center justify-center px-6"
      style={{ animation: "ncFadeIn 0.25s ease both" }}>
      {stage === "cover" ? (
        <button data-testid="lock-cover" onClick={() => { sfx?.keyTick?.(); setStage("unlock"); }} className="flex flex-col items-center gap-3 w-full h-full justify-center">
          <Lock className="w-5 h-5 text-white/60" />
          <p className="font-sans font-extralight text-6xl text-white tracking-tight">{now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">{now.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}</p>
          {(unread > 0 || mailUnread > 0) && (
            <span className="mt-2 flex items-center gap-1.5 font-mono text-[9px] text-white/50 border border-white/15 rounded-full px-3 py-1.5">
              <Bell className="w-3 h-3" />{unread + mailUnread} waiting
            </span>
          )}
          <p className="mt-10 font-mono text-[9px] uppercase tracking-[0.35em] text-white/40 animate-pulse">tap to unlock</p>
        </button>
      ) : (
        <div className="flex flex-col items-center gap-5" style={shakeStyle}>
          <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/70">
            {method === "pin" ? "enter passcode" : method === "pattern" ? "draw your pattern" : "verify identity"}
          </p>
          {cool > 0 ? (
            <p data-testid="lock-cooldown" className="font-mono text-sm text-red-300">Locked out — {cool}s</p>
          ) : method === "pin" ? (
            <PinPad entry={pin} onDigit={digit} onDelete={() => setPin((p) => p.slice(0, -1))} accent={accent} disabled={cool > 0} />
          ) : method === "pattern" ? (
            <PatternPad points={pat} onToggle={toggleDot} accent={accent} disabled={cool > 0} />
          ) : (
            <ThumbScan onDone={succeed} accent={accent} disabled={cool > 0} />
          )}
        </div>
      )}
    </div>
  );
}
